import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { CompanyApiService } from '@api-services/company-api.service';
import { REDIRECT_TO_APPS_PARAM_NAME } from '@constants/redirect-to-apps-param-name.constants';
import { AppStoreState } from '@interfaces/app-store-state';
import { companySelector } from '@modules/company/company-core/store/company/company-selector';
import { AuthService } from '@modules/core/auth/auth.service';
import { AppConfigService } from '@services/app-config.service';

export type AppsAccessExtraParams = Record<string, string>;

@Injectable({
  providedIn: 'root'
})
export class AppsAccessService {
  constructor(
    private readonly store: Store<AppStoreState>,
    private readonly companyApiService: CompanyApiService,
    private readonly appConfigService: AppConfigService,
    private readonly authService: AuthService
  ) {}

  openApps(companyId?: number, extraParams?: AppsAccessExtraParams): void {
    this.openApps$(companyId, extraParams).subscribe();
  }

  openAppsWithCurrentCompany(extraParams?: AppsAccessExtraParams): void {
    this.store
      .select(companySelector)
      .pipe(
        take(1),
        switchMap(({ id: companyId }) => this.openApps$(companyId, extraParams))
      )
      .subscribe();
  }

  private openApps$(companyId?: number, extraParams?: AppsAccessExtraParams): Observable<unknown> {
    const url = new URL(`${this.appConfigService.environment.APPS_URL}/connect-as`);
    const accessToken = this.authService.accessToken;

    url.searchParams.set('access_token', accessToken);
    if (extraParams) {
      for (const [key, value] of Object.entries(extraParams)) {
        if (key === REDIRECT_TO_APPS_PARAM_NAME && companyId) {
          url.searchParams.set(key, this.addCompaniesToUrl(companyId, value));
        } else {
          url.searchParams.set(key, value);
        }
      }
    }

    return this.companyApiService.clientAccess(companyId).pipe(tap(() => window.open(url.toString(), '_blank')));
  }

  /**
   * Préfixe le path de redirection avec le dossier fourni
   *
   * @param companyId
   * @param redirectTo
   * @private
   */
  private addCompaniesToUrl(companyId: number, redirectTo: string): string {
    if (!companyId) {
      return redirectTo;
    }
    if (redirectTo.match(/^\/?companies\/.*/gi)) {
      return redirectTo;
    }

    return `companies/${companyId}/${redirectTo}`.replace(/\/\//gim, '/'); // On remplace les doubles slashs
  }
}
